import type { LucideIcon } from 'lucide-react'

import {
    BadgeCheck,
    ClipboardCheck,
    Crown,
    Gem,
    Globe,
    Handshake,
    Music,
    Palette,
    Presentation,
    Route,
    Trophy,
    Users
} from 'lucide-react'
import { CDN_BASE } from '@/constants/site'

type IconCopy = {
    icon: LucideIcon
    title: string
    text: string
}

type ImageCopy = {
    image: string
    alt: string
    title: string
    text: string
}

export const heroImages = {
    main: {
        src: `${CDN_BASE}/images/gallery/wedding-event-2.jpg`,
        alt: 'White draped wedding mandap with rose garlands, lit at dusk beside a tiered diya lamp'
    },
    concert: {
        src: `${CDN_BASE}/images/gallery/coldplay-concert-1.png`,
        alt: 'Packed stadium crowd lit in red and pink during a Coldplay concert'
    },
    stadium: {
        src: `${CDN_BASE}/images/gallery/namo-stadium-manpower.png`,
        alt: 'Guju Events staff lined up outside Narendra Modi Stadium during the ICC T20 World Cup'
    }
}

export const aboutImages = {
    home: {
        src: `${CDN_BASE}/images/site/about-home.jpg`,
        alt: 'Wedding couple under a floral mandap with the Guju Events hospitality, catering and production team'
    },
    story: {
        src: `${CDN_BASE}/images/site/about-story.jpg`,
        alt: 'Golden wedding stage, a cricket trophy and a corporate meeting, the three worlds Guju Events works in'
    }
}

export const eventTypesMarquee: string[] = [
    'Luxury Weddings',
    'Garba Nights',
    'Live Concerts',
    'IPL Manpower',
    'Corporate Launches',
    'Exhibitions',
    'Baby Showers',
    'Brand Activations',
    'Religious Events',
    'Award Nights'
]

export const highlights: IconCopy[] = [
    {
        icon: Trophy,
        title: 'Narendra Modi Stadium',
        text: "IPL and T20 World Cup match day teams at the world's largest cricket stadium."
    },
    {
        icon: Music,
        title: 'Coldplay to Arijit Singh',
        text: 'Crowd and entry teams on the ground for headline concerts.'
    },
    {
        icon: Crown,
        title: 'Laxmi Vilas Palace',
        text: "Navratri nights at one of India's grandest palace grounds."
    }
]

export const brandHighlightIcon: LucideIcon = BadgeCheck

export const whyChooseUs: IconCopy[] = [
    {
        icon: Palette,
        title: 'Creative concept and design',
        text: 'Every event starts with a concept built around you, from themes and layouts to décor that photographs beautifully.'
    },
    {
        icon: Handshake,
        title: 'Premium vendor network',
        text: 'Trusted décor, sound, lighting, catering and logistics partners, chosen for quality and reliability.'
    },
    {
        icon: ClipboardCheck,
        title: 'Flawless execution',
        text: 'Detailed run sheets, trained crews and on-site coordinators keep every moment on time and on brief.'
    },
    {
        icon: Globe,
        title: 'Pan India presence',
        text: 'Based in Ahmedabad and delivering events across Gujarat, Mumbai, Pune, Delhi, Bangalore and Rajasthan.'
    }
]

export const processSteps: { title: string, text: string }[] = [
    {
        title: 'Consultation and planning',
        text: 'We listen to your vision, goals, guest list and budget, then build a personalised roadmap for the event.'
    },
    {
        title: 'Creative concept design',
        text: 'Themes, layouts and curated décor ideas, presented as a clear concept you can react to before anything is booked.'
    },
    {
        title: 'Vendor and logistics setup',
        text: 'We line up vendors, staff, technical setups and on-site logistics, and confirm every detail in a single run sheet.'
    },
    {
        title: 'Flawless execution',
        text: 'Our coordinators run the day on the ground so you can enjoy it, from the first arrival to the final pack down.'
    }
]

export const industries: string[] = [
    'Weddings',
    'Corporate Events',
    'Government Projects',
    'Sports Events',
    'Religious Functions',
    'Exhibitions',
    'Brand Activations'
]

export const aboutValues: IconCopy[] = [
    {
        icon: Gem,
        title: 'Luxury experience',
        text: 'Thoughtful design, premium materials and hospitality that makes every guest feel looked after.'
    },
    {
        icon: Users,
        title: 'Expert team',
        text: 'Planners, designers, coordinators and trained crews who handle stadium crowds and family functions alike.'
    },
    {
        icon: Route,
        title: 'Pan India coverage',
        text: 'From Ahmedabad to Mumbai, Delhi and beyond, we bring the same standards wherever your event is.'
    },
    {
        icon: ClipboardCheck,
        title: 'Premium execution',
        text: 'Clear timelines, rehearsed run sheets and calm problem solving on the day, so nothing is left to chance.'
    }
]

export const venues: ImageCopy[] = [
    {
        image: `${CDN_BASE}/images/gallery/namo-stadium-manpower.png`,
        alt: 'Guju Events staff in yellow vests outside Narendra Modi Stadium during the ICC T20 World Cup',
        title: 'Narendra Modi Stadium, Ahmedabad',
        text: 'T20 World Cup and IPL match day teams'
    },
    {
        image: `${CDN_BASE}/images/gallery/navratri-laxmi-vilas-palace-2.png`,
        alt: 'Guju Events staff in red jackets at the entry turnstiles of a Navratri ground at night',
        title: 'Laxmi Vilas Palace, Vadodara',
        text: 'Navratri entry, ticketing and crowd management'
    },
    {
        image: `${CDN_BASE}/images/gallery/coldplay-concert-1.png`,
        alt: 'Packed stadium crowd lit in red and pink during a Coldplay concert',
        title: 'Headline concert stages',
        text: 'Coldplay, Enrique Iglesias, Arijit Singh, B Praak and Rolling Loud'
    }
]

export const collaborations: IconCopy[] = [
    {
        icon: Presentation,
        title: 'Samsung launch event',
        text: 'Managed an end-to-end product unveiling across Gujarat with stage, production and manpower.'
    },
    {
        icon: Trophy,
        title: 'IPL manpower operations',
        text: 'Delivered stadium-level coordination and staffing support on match days.'
    },
    {
        icon: ClipboardCheck,
        title: 'Government exam setup',
        text: 'Managed exam manpower for thousands of candidates, smoothly and on schedule.'
    }
]

export const guestOptions: string[] = [
    'Under 100',
    '100 to 300',
    '300 to 750',
    '750 to 2,000',
    '2,000 or more'
]

export const budgetOptions: string[] = [
    'Under ₹2 lakh',
    '₹2 to 5 lakh',
    '₹5 to 15 lakh',
    '₹15 to 50 lakh',
    '₹50 lakh or more',
    'Not sure yet'
]
